(() => {
  const VERSION = '2026-08-10.2';
  const ROOT_SELECTOR = '#avontic-primary-product';
  let timer = 0;
  let stickyFrame = 0;
  let observer;
  let introObserver;

  const optionName = (option) => {
    const label = option.querySelector('legend, .variant-picker__option-info, label');
    return String(label?.textContent || '').trim().toLowerCase();
  };

  const moveModelBeforeFinish = (root) => {
    const picker = root.querySelector('[data-block-id="variant_picker"] variant-picker');
    if (!picker) return;

    const options = [...picker.children].filter((node) => node.matches('fieldset, .variant-picker__option'));
    const model = options.find((node) => /(size|model|device|generation|series)/i.test(optionName(node)));
    const finish = options.find((node) => /(color|colour|finish)/i.test(optionName(node)));

    if (model && finish && model.nextElementSibling !== finish) {
      picker.insertBefore(model, finish);
    }
  };

  const normalizeMoney = (value) => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    const symbolAmount = text.match(/[$£€]\s*\d[\d,]*(?:\.\d{2})?/);
    if (symbolAmount) return symbolAmount[0].replace(/\s+/g, '');
    const codedAmount = text.match(/\d[\d,]*(?:\.\d{2})?\s*(?:USD|CAD|AUD|NZD|GBP|EUR)/i);
    return codedAmount ? codedAmount[0].replace(/\s+/g, ' ') : '';
  };

  const currentPrice = (root) => {
    const sources = [
      root.querySelector('[data-block-id="price"] sale-price'),
      root.querySelector('[data-block-id="buy_buttons"] [data-av-pdp-price]'),
      root.querySelector('[data-block-id="price"] .price-list')
    ];
    for (const source of sources) {
      const price = normalizeMoney(source?.textContent);
      if (price) return price;
    }
    return '';
  };

  const enhanceAddButton = (root) => {
    const button = root.querySelector('[data-block-id="buy_buttons"] button[type="submit"][data-av-atc-control]');
    if (!button || button.disabled) return;

    const visibleLabel = String(button.textContent || '').replace(/\s+/g, ' ').trim();
    const rawBase = String(button.dataset.avRgBaseLabel || visibleLabel);
    const baseLabel = rawBase.split(/[·•]/)[0].replace(/\s+price\s*$/i, '').trim();
    if (!/add to cart/i.test(baseLabel)) return;
    button.dataset.avRgBaseLabel = baseLabel;

    const price = currentPrice(root);
    if (!price) return;

    const nextLabel = baseLabel; // Item subtotal is shown beside the quantity selector.
    if (visibleLabel !== nextLabel) button.textContent = nextLabel;
  };

  const refineDeliveryCopy = (root) => {
    const note = root.querySelector('.av-delivery-estimate__fine-print');
    if (!note || note.dataset.avRgRefined === 'true') return;

    note.textContent = 'Delivery estimates can vary by carrier, destination, customs, season, and remote-area service.';
    note.dataset.avRgRefined = 'true';
  };

  const enforceStickyTiming = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    const story = root?.querySelector('[data-av-rg-buybox]');
    const primary = root?.querySelector('[data-block-id="buy_buttons"] button[type="submit"][data-av-atc-control]');
    if (!story || !primary) return;

    const productId = String(story.dataset.productId || '');
    const purchaseRect = primary.getBoundingClientRect();
    const header = document.querySelector('header, .shopify-section--header');
    const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
    const hasPassedPrimary = purchaseRect.bottom <= headerBottom;

    document.querySelectorAll('.av-pdp-sticky-cockpit').forEach((sticky) => {
      if (productId && String(sticky.dataset.productId || '') !== productId) return;
      sticky.dataset.avRgPassed = hasPassedPrimary ? 'true' : 'false';
    });
  };

  const scheduleSticky = () => {
    window.cancelAnimationFrame(stickyFrame);
    stickyFrame = window.requestAnimationFrame(enforceStickyTiming);
  };

  const armRhinoIntro = (root) => {
    const story = root.querySelector('[data-av-rg-buybox]');
    if (!story || story.dataset.avRgIntroArmed === 'true') return;

    story.dataset.avRgIntroArmed = 'true';
    const storageKey = `avontic:rg-intro:${story.dataset.productId || location.pathname}`;

    try {
      if (sessionStorage.getItem(storageKey) === 'played') return;
    } catch (_) {
      // The one-time animation still works if session storage is unavailable.
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const play = () => {
      const mark = story.querySelector('.av-rg-buybox__mark img');
      if (!mark || story.classList.contains('is-av-rg-intro')) return;

      story.classList.add('is-av-rg-intro');
      try {
        sessionStorage.setItem(storageKey, 'played');
      } catch (_) {
        // Nothing else is required when storage is unavailable.
      }

      const finish = (event) => {
        if (event.target !== mark || event.animationName !== 'avRgRhinoHeadbutt') return;
        mark.removeEventListener('animationend', finish);
        story.classList.remove('is-av-rg-intro');
      };
      mark.addEventListener('animationend', finish);
    };

    if (!('IntersectionObserver' in window)) {
      play();
      return;
    }

    introObserver?.disconnect();
    introObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      introObserver.disconnect();
      play();
    }, { threshold: 0.35, rootMargin: '0px 0px -8% 0px' });
    introObserver.observe(story);
  };

  const hydrate = () => {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root?.querySelector('[data-av-rg-buybox]')) return;

    root.classList.add('av-rg-template');
    root.dataset.avRgLegendary = VERSION;
    moveModelBeforeFinish(root);
    enhanceAddButton(root);
    refineDeliveryCopy(root);
    armRhinoIntro(root);
    scheduleSticky();

    if (!observer) {
      observer = new MutationObserver(() => schedule());
      observer.observe(root, { childList: true, subtree: true, characterData: true });
    }
  };

  const schedule = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(hydrate, 40);
  };

  document.addEventListener('change', schedule, true);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('avontic:pdp-fit-verification', schedule);
  window.addEventListener('scroll', scheduleSticky, { passive: true });
  window.addEventListener('resize', scheduleSticky, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrate, { once: true });
  } else {
    hydrate();
  }
})();
